/**
 * RefundEditor — record-only refund flow for a CRM ticket.
 *
 * Loads the parent ticket's order via `useOrderDetail` and renders a line
 * picker; submit is gated on at least one selected item with quantity > 0.
 * Approval / reject lives in the same panel, mirroring the API's two
 * endpoints: PATCH for items + amounts, POST /decision/ for the verdict.
 */
import { useEffect, useMemo, useState } from 'react';
import { Banknote, CheckCircle2, Info, Receipt, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { cn } from '@/lib/cn';
import { fmt } from '@/lib/format';
import { REFUND_DECISION_TONE } from '@/lib/crmStatus';
import { isPrepaidOnlinePayment } from '@/lib/refundTicket';
import { notify } from '@/lib/toast';
import { useOrderDetail, type OrderDetail } from '@/api/legacy/orders';
import {
  useCreateCrmRefund,
  useDecideCrmRefund,
  usePatchCrmRefund,
  type CrmRefundItem,
  type CrmTicket,
  type RefundItemInput,
} from '@/api/bi/crm';

export interface DraftLine {
  key?: string;
  order_item_id: number;
  product_name: string;
  quantity_in_order: number;
  unit_price: number;
  selected: boolean;
  quantity: number;
  note: string;
  is_edited?: boolean;
  action?: string;
}

export function getIsPrepaidOnline(
  orderDetailData: OrderDetail | null | undefined,
  ticket?: CrmTicket | null,
): boolean {
  if (orderDetailData?.order?.is_prepaid_online != null) {
    return Boolean(orderDetailData.order.is_prepaid_online);
  }
  if ((orderDetailData as { is_prepaid_online?: boolean } | undefined)?.is_prepaid_online != null) {
    return Boolean((orderDetailData as { is_prepaid_online?: boolean }).is_prepaid_online);
  }
  return isPrepaidOnlinePayment(
    orderDetailData?.order?.payment_type ??
      ticket?.order?.payment_type ??
      ticket?.refund?.payment_type_at_order,
  );
}

export function isDraftLineDisabled(
  line: DraftLine,
  isPrepaidOnline: boolean,
  isDecided: boolean,
): boolean {
  if (isDecided) return true;
  // Only edited items should be disabled in case of not pre payment, not the order_items
  if (!isPrepaidOnline && line.is_edited) {
    return true;
  }
  return false;
}

export function buildDraftLines(
  orderDetailData: OrderDetail | null | undefined,
  refundItems?: CrmRefundItem[] | null,
): DraftLine[] {
  if (!orderDetailData) return [];
  const items = orderDetailData.order_items ?? [];
  const editedItems = orderDetailData.edited_order_items ?? [];
  const byOI = new Map(refundItems?.map((i) => [i.order_item_id, i]) ?? []);
  const matchedOI = new Set<number>();

  const orderLines: DraftLine[] = items.map((it) => {
    const existing = byOI.get(it.id);
    if (existing) matchedOI.add(it.id);
    const unit = Number(it.price_with_vat) || Number(it.base_price) || 0;
    return {
      key: `oi-${it.id}`,
      order_item_id: it.id,
      product_name: it.product_name ?? `Item #${it.id}`,
      quantity_in_order: it.quantity,
      unit_price: unit,
      selected: !!existing,
      quantity: existing ? existing.quantity : it.quantity,
      note: existing?.note ?? '',
    };
  });

  const next: DraftLine[] = [...orderLines];

  for (const it of editedItems) {
    const oiId = (it.order_item_id && it.order_item_id > 0) ? it.order_item_id : it.id;
    const existingLineIndex =
      it.order_item_id != null
        ? next.findIndex((l) => l.order_item_id === it.order_item_id)
        : -1;

    if (existingLineIndex >= 0) {
      const currentLine = next[existingLineIndex];
      const updatedQty =
        it.new_quantity != null && it.new_quantity > 0
          ? it.new_quantity
          : currentLine.quantity_in_order;
      const updatedUnit = Number(it.unit_price) || currentLine.unit_price;
      next[existingLineIndex] = {
        ...currentLine,
        quantity_in_order: updatedQty,
        unit_price: updatedUnit,
        quantity: currentLine.selected ? currentLine.quantity : updatedQty,
        is_edited: true,
        action: it.action,
      };
    } else {
      const existing = byOI.get(oiId);
      if (existing) matchedOI.add(oiId);
      const qtyInOrder = Math.max(
        1,
        it.new_quantity != null && it.new_quantity > 0
          ? it.new_quantity
          : Math.abs(it.quantity_delta) || it.previous_quantity || 1,
      );
      const unit = Number(it.unit_price) || 0;
      next.push({
        key: `eoi-${it.id}`,
        order_item_id: oiId,
        product_name: it.product_name || `Item #${oiId}`,
        quantity_in_order: qtyInOrder,
        unit_price: unit,
        selected: !!existing,
        quantity: existing ? existing.quantity : qtyInOrder,
        note: existing?.note ?? '',
        is_edited: true,
        action: it.action,
      });
    }
  }

  if (refundItems) {
    for (const ri of refundItems) {
      if (!matchedOI.has(ri.order_item_id)) {
        next.push({
          key: `ref-${ri.id ?? ri.order_item_id}`,
          order_item_id: ri.order_item_id,
          product_name: ri.product_name ?? `Item #${ri.order_item_id}`,
          quantity_in_order: ri.quantity_in_order ?? ri.quantity,
          unit_price: Number(ri.unit_price_snapshot) || 0,
          selected: true,
          quantity: ri.quantity,
          note: ri.note ?? '',
        });
      }
    }
  }

  return next;
}

export function RefundEditor({ ticket }: { ticket: CrmTicket }) {
  const refund = ticket.refund ?? null;
  const orderId = ticket.order_id ?? null;
  const orderDetail = useOrderDetail(orderId);

  const create = useCreateCrmRefund();
  const patch = usePatchCrmRefund(refund?.id);
  const decide = useDecideCrmRefund(refund?.id ?? -1);

  const isDecided = !!refund && refund.decision !== 'pending';
  const isPrepaidOnline = getIsPrepaidOnline(orderDetail.data, ticket);

  const [requestedAmount, setRequestedAmount] = useState<string>(
    refund?.requested_amount ?? '',
  );
  const [customerReason, setCustomerReason] = useState<string>(refund?.customer_reason ?? '');
  const [internalNotes, setInternalNotes] = useState<string>(refund?.internal_notes ?? '');
  const [lines, setLines] = useState<DraftLine[]>(() =>
    buildDraftLines(orderDetail.data, refund?.items),
  );

  // Build draft lines from order items + edited order items + any existing refund lines.
  useEffect(() => {
    if (!orderDetail.data) return;
    setLines(buildDraftLines(orderDetail.data, refund?.items));
  }, [orderDetail.data, refund?.items]);

  useEffect(() => {
    setRequestedAmount(refund?.requested_amount ?? '');
    setCustomerReason(refund?.customer_reason ?? '');
    setInternalNotes(refund?.internal_notes ?? '');
  }, [refund?.id]);

  const hasEditedItems = useMemo(() => lines.some((l) => l.is_edited), [lines]);

  const selected = lines.filter((l) => {
    if (!l.selected || l.quantity <= 0) return false;
    if (isDecided) return true;
    return !isDraftLineDisabled(l, isPrepaidOnline, false);
  });

  const computedTotal = useMemo(
    () =>
      selected.reduce((sum, l) => sum + l.unit_price * (l.quantity || 0), 0),
    [selected],
  );

  function lineToInput(l: DraftLine): RefundItemInput {
    return {
     [l.is_edited && l.action=="deleted" ?"order_item_history_id":  "order_item_id"]: l.order_item_id,
      quantity: l.quantity,
      unit_price_snapshot: l.unit_price.toFixed(2),
      line_amount: (l.unit_price * l.quantity).toFixed(2),
      note: l.note,
    };
  }

  async function handleSave() {
    if (orderId == null) {
      notify.error('Refund tickets must be tagged to an order.');
      return;
    }
    if (selected.length === 0) {
      notify.error('Pick at least one product line and quantity.');
      return;
    }
    const items = selected.map(lineToInput);
  
    const amount = requestedAmount || computedTotal.toFixed(2);
    try {
      if (refund) {
        await patch.mutateAsync({
          requested_amount: amount,
          customer_reason: customerReason,
          internal_notes: internalNotes,
          items,
        });
        notify.success('Refund updated.');
      } else {
        await create.mutateAsync({
          ticket_id: ticket.id,
          requested_amount: amount,
          customer_reason: customerReason,
          internal_notes: internalNotes,
          items,
        });
        notify.success('Refund created — pending approval.');
      }
    } catch (err) {
      notify.error(extractError(err) || 'Could not save refund.');
    }
  }

  async function handleDecision(decision: 'approved' | 'rejected') {
    if (!refund) return;
    if (decision === 'approved' && selected.length === 0) {
      notify.error('Add at least one product line before approving.');
      return;
    }
    try {
      await decide.mutateAsync({
        decision,
        approved_amount: decision === 'approved' ? requestedAmount || computedTotal.toFixed(2) : undefined,
        note: internalNotes,
      });
      notify.success(`Refund ${decision}.`);
    } catch (err) {
      notify.error(extractError(err) || `Could not ${decision === 'approved' ? 'approve' : 'reject'}.`);
    }
  }

  if (orderId == null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="inline-flex items-center gap-2">
            <Receipt className="h-4 w-4" /> Refund
          </CardTitle>
        </CardHeader>
        <CardBody>
          <p className="text-xs text-muted-foreground">
            Refund tickets must be tagged to an order. Edit the parent ticket to attach an order
            first.
          </p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2">
          <Receipt className="h-4 w-4" /> Refund
        </CardTitle>
        <div className="ml-auto flex items-center gap-1.5">
          {isPrepaidOnline ? (
            <Badge tone="primary" title="Prepaid online order (CIPS / Fonepay)">
              Prepaid online
            </Badge>
          ) : (
            <Badge tone="neutral" title="Cash on Delivery / offline payment">
              COD / Offline
            </Badge>
          )}
          {refund ? (
            <Badge tone={REFUND_DECISION_TONE[refund.decision]}>{refund.decision}</Badge>
          ) : (
            <Badge tone="neutral">Draft</Badge>
          )}
          {refund?.settlement ? (
            <Badge
              tone={
                refund.settlement.status === 'completed'
                  ? 'success'
                  : refund.settlement.status === 'cancelled'
                    ? 'neutral'
                    : 'warning'
              }
              title="Accounts settlement status"
            >
              settled: {refund.settlement.status}
            </Badge>
          ) : refund ? (
            <Badge tone="warning" title="No payment.Refund linked yet">
              unsettled
            </Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardBody className="space-y-3">
        {orderDetail.isLoading ? (
          <p className="text-xs text-muted-foreground">Loading order items…</p>
        ) : null}
        {orderDetail.isError ? (
          <p className="text-xs text-danger">Could not load order items.</p>
        ) : null}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Customer reason
            </label>
            <textarea
              value={customerReason}
              onChange={(e) => setCustomerReason(e.target.value)}
              rows={2}
              placeholder="What did the customer say?"
              className="w-full rounded-md border border-input bg-surface px-3 py-2 text-xs"
              disabled={isDecided}
            />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Internal notes
            </label>
            <textarea
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              rows={2}
              placeholder="Approval rationale, follow-ups, etc."
              className="w-full rounded-md border border-input bg-surface px-3 py-2 text-xs"
            />
          </div>
        </div>

        {!isPrepaidOnline && hasEditedItems ? (
          <div className="flex items-center gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning-soft">
            <Info className="h-4 w-4 shrink-0" />
            <span>
              This order was not prepaid online (COD). Edited order items are disabled for refund.
            </span>
          </div>
        ) : null}

        {/* Items */}
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5 text-left">Refund</th>
                <th className="px-2 py-1.5 text-left">Item</th>
                <th className="px-2 py-1.5 text-right">In order</th>
                <th className="px-2 py-1.5 text-right">Qty</th>
                <th className="px-2 py-1.5 text-right">Unit price</th>
                <th className="px-2 py-1.5 text-right">Line</th>
                <th className="px-2 py-1.5 text-left">Note</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => {
                const isRowDisabled = isDraftLineDisabled(
                  l,
                  isPrepaidOnline,
                  isDecided,
                );
                return (
                  <tr
                    key={l.key ?? `${l.order_item_id}-${idx}`}
                    className={cn(
                      'border-t border-border',
                      isRowDisabled && 'opacity-60 bg-muted/10',
                    )}
                    title={
                      isDecided
                        ? 'Refund already decided'
                        : isRowDisabled
                          ? 'Edited order items cannot be selected for refund on COD orders'
                          : undefined
                    }
                  >
                    <td className="px-2 py-1.5">
                      <input
                        type="checkbox"
                        checked={l.selected}
                        onChange={(e) =>
                          setLines((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, selected: e.target.checked } : row,
                            ),
                          )
                        }
                        disabled={isRowDisabled}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1.5">
                        <span>{l.product_name}</span>
                        {l.is_edited ? (
                          <Badge
                            tone={
                              l.action === 'added'
                                ? 'primary'
                                : l.action === 'removed'
                                  ? 'danger'
                                  : 'warning'
                            }
                            className="capitalize text-[10px] px-1.5 py-0"
                          >
                            {l.action || 'edited'}
                          </Badge>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-right">{l.quantity_in_order}</td>
                    <td className="px-2 py-1.5 text-right">
                      <input
                        type="number"
                        min={1}
                        max={l.quantity_in_order}
                        value={l.quantity}
                        onChange={(e) =>
                          setLines((prev) =>
                            prev.map((row, i) =>
                              i === idx
                                ? { ...row, quantity: Math.max(1, Number(e.target.value || 0)) }
                                : row,
                            ),
                          )
                        }
                        className="h-7 w-16 rounded-sm border border-input bg-surface px-2 text-right"
                        disabled={!l.selected || isRowDisabled}
                      />
                    </td>
                    <td className="px-2 py-1.5 text-right">{fmt.npr(l.unit_price)}</td>
                    <td className="px-2 py-1.5 text-right">
                      {fmt.npr(l.unit_price * (l.selected ? l.quantity : 0))}
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        value={l.note}
                        onChange={(e) =>
                          setLines((prev) =>
                            prev.map((row, i) => (i === idx ? { ...row, note: e.target.value } : row)),
                          )
                        }
                        placeholder="Optional"
                        className="h-7 w-full rounded-sm border border-input bg-surface px-2"
                        disabled={!l.selected || isRowDisabled}
                      />
                    </td>
                  </tr>
                );
              })}
              {lines.length === 0 ? (
                <tr>
                  <td className="px-2 py-3 text-center text-muted-foreground" colSpan={7}>
                    No items found on this order.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="flex-1 text-[11px] text-muted-foreground">
            Selected {selected.length} line{selected.length === 1 ? '' : 's'} · computed total
            <span className="ml-1 kpi-num text-foreground">{fmt.npr(computedTotal)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Banknote className="h-4 w-4 text-muted-foreground" />
            <input
              value={requestedAmount}
              onChange={(e) => setRequestedAmount(e.target.value.replace(/[^\d.]/g, ''))}
              placeholder={computedTotal.toFixed(2)}
              className="h-9 w-32 rounded-md border border-input bg-surface px-3 text-right text-sm"
              disabled={isDecided}
            />
            <span className="text-xs text-muted-foreground">requested</span>
          </div>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={create.isPending || patch.isPending || isDecided}
          >
            {refund ? (patch.isPending ? 'Saving…' : 'Update refund') : create.isPending ? 'Saving…' : 'Create refund'}
          </Button>
        </div>

        {refund?.decision === 'pending' ? (
          <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleDecision('rejected')}
              disabled={decide.isPending}
              className="gap-1"
            >
              <XCircle className="h-3.5 w-3.5" /> Reject
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleDecision('approved')}
              disabled={decide.isPending}
              className="gap-1"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Approve refund
            </Button>
          </div>
        ) : null}

        {refund && refund.decision !== 'pending' ? (
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
            {refund.decision === 'approved' ? 'Approved' : 'Rejected'}
            {refund.decided_by ? ` by ${refund.decided_by.name}` : ''}
            {refund.decided_at ? ` on ${fmt.datetime(refund.decided_at)}` : ''}
            . Hand off to accounts to post the CBMS credit note and refund the customer.
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function extractError(err: unknown): string | null {
  const data = (err as { response?: { data?: Record<string, unknown> } }).response?.data;
  if (!data) return null;
  if (typeof data.detail === 'string') return data.detail;
  // pydantic-ish field error map → first message
  for (const v of Object.values(data)) {
    if (typeof v === 'string') return v;
    if (Array.isArray(v) && typeof v[0] === 'string') return v[0];
  }
  return null;
}
